import { Fragment, ReactElement, useState } from "react";
import { ViewerApi } from "@bronte/viewer-api/src/main";

import { Menu } from "../Menu";
import { TocDrawer } from "../TocDrawer";
import { SettingsDrawer } from "../SettingsDrawer";
import { NotebookDrawer } from "../NotesDrawer";
import { FlashcardsDrawer } from "../FlashcardsDrawer";
import { AudioBookDrawer } from "../AudioBookDrawer";
import { SearchDrawer } from "../SearchDrawer";

import { DrawerTypes, SpotlightAudio } from "../../shared/types";
import "./styles.scss";

interface IAsideBar {
  tocNodeHtml: string;
  viewerApi: ViewerApi;
  renderedId: string;
  annotationUpdated: boolean;
  parentActivityId?: string;
  onSpotlightClick: (data: SpotlightAudio) => void;
}

const AsideBar = ({
  annotationUpdated,
  tocNodeHtml,
  viewerApi,
  renderedId,
  parentActivityId,
  onSpotlightClick,
}: IAsideBar): ReactElement => {
  const [showToc, setShowToc] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNotebook, setShowNotebook] = useState(false);
  const [showFlashcards, setShowFlashcards] = useState(false);
  const [showAudioBook, setShowAudioBook] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const handleDrawers = (type: DrawerTypes | null): void => {
    switch (type) {
      case DrawerTypes.TOC:
        setShowSettings(false);
        setShowNotebook(false);
        setShowToc(!showToc);
        setShowFlashcards(false);
        setShowAudioBook(false);
        setShowSearch(false);
        break;
      case DrawerTypes.SETTINGS:
        setShowToc(false);
        setShowNotebook(false);
        setShowSettings(!showSettings);
        setShowFlashcards(false);
        setShowAudioBook(false);
        setShowSearch(false);
        break;
      case DrawerTypes.NOTEBOOK:
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(!showNotebook);
        setShowFlashcards(false);
        setShowAudioBook(false);
        setShowSearch(false);
        break;
      case DrawerTypes.AUDIOBOOK:
        viewerApi.media().pause();
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(false);
        setShowFlashcards(false);
        setShowAudioBook(!showAudioBook);
        setShowSearch(false);
        break;
      case DrawerTypes.FLASHCARD:
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(false);
        setShowFlashcards(!showFlashcards);
        setShowAudioBook(false);
        setShowSearch(false);
        break;
      case DrawerTypes.SEARCH:
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(false);
        setShowFlashcards(false);
        setShowAudioBook(false);
        setShowSearch(!showSearch);
        break;
      default:
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(false);
        setShowFlashcards(false);
        setShowSearch(false);
        break;
    }
    viewerApi?.search().clear();
  };

  const closeSearchDrawer = (): void => {
    viewerApi?.search().clear();
    setShowSearch(false);
  };

  return (
    <Fragment>
      <Menu
        onMenuItemClick={handleDrawers}
        parentActivityId={parentActivityId}
        viewerApi={viewerApi}
      />

      {showToc && (
        <TocDrawer onClose={(): void => setShowToc(false)} renderedId={renderedId}>
          {tocNodeHtml}
        </TocDrawer>
      )}

      {showSettings && (
        <SettingsDrawer viewerApi={viewerApi} onClose={(): void => setShowSettings(false)} />
      )}

      {showNotebook && (
        <NotebookDrawer
          annotationUpdated={annotationUpdated}
          onClose={(): void => setShowNotebook(false)}
        />
      )}

      {showFlashcards && (
        <FlashcardsDrawer
          annotationUpdated={annotationUpdated}
          onClose={(): void => setShowFlashcards(false)}
        />
      )}

      {showAudioBook && (
        <AudioBookDrawer
          onSpotlightClick={onSpotlightClick}
          onClose={(): void => setShowAudioBook(false)}
        />
      )}

      {showSearch && <SearchDrawer viewerApi={viewerApi} onClose={closeSearchDrawer} />}
    </Fragment>
  );
};

export default AsideBar;
